import {
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Divider,
  Spinner,
  Center,
  Box,
} from "@chakra-ui/react";
import { AudienceList, DynamicListType } from "../../common/types/campaign";
import { convertCase } from "../../common/helper/commonHelper";
import urls from "../../urls";
import { SearchField } from "../SearchField";
import { debounce } from "lodash";
import { useState, useCallback, useMemo } from "react";
import { getDynamicListPreview } from "../../pages/dashboard/campaign/campaignSlice";
import { useAppDispatch } from "../../store";
import { FILTER_COLUMNS_DL_PREVIEW } from "../../common/constants/campaign";

export default function DynamicListPreview({
  loading,
  data,
  dynamicListQuery,
  isOpen,
  onClose,
}: {
  loading: boolean;
  data: AudienceList | null;
  dynamicListQuery: DynamicListType[];
  isOpen: boolean;
  onClose: () => void;
}) {
  const columnKeys = useMemo(
    () => (data && data.rows.length ? Object.keys(data.rows[0]).sort() : []),
    [data]
  );
  const [searchKeyword, setSearchKeyword] = useState("");
  const dispatch = useAppDispatch();

  const searchIfValid = useCallback(
    (keyword: string) => {
      dispatch(
        getDynamicListPreview({
          query: dynamicListQuery,
          filterColumns: FILTER_COLUMNS_DL_PREVIEW,
          searchKeyword: keyword,
        })
      );
    },
    [dispatch, dynamicListQuery]
  );

  const debounceSearch = useMemo(
    () => debounce(searchIfValid, 1000),
    [searchIfValid]
  );

  const onSearch = useCallback(
    (keyword: string) => {
      setSearchKeyword(keyword);
      debounceSearch(keyword.trim());
    },
    [debounceSearch]
  );

  function onClosePreviewModal() {
    setSearchKeyword("");
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClosePreviewModal} isCentered>
      <ModalOverlay />
      <ModalContent minW="6xl" minH="650px">
        <ModalHeader p="5" position={"relative"}>
          <Flex direction="column">
            <Heading fontSize="lg" as="h4">
              Dynamic List
            </Heading>
            {!loading && data && (
              <Text fontSize="sm" fontWeight="normal" display="flex">
                Previewing
                <Text as="span" color="blue.400" fontWeight="semibold" mx="1">
                  {data.rows.length}
                </Text>
                of
                <Text as="span" color="blue.400" fontWeight="semibold" mx="1">
                  {data.total_count.count}
                </Text>
                people
              </Text>
            )}
          </Flex>
          <Box width="270px" position="absolute" right={12} top={2.5}>
            <SearchField
              name="search-dynamic-list"
              value={searchKeyword}
              onSearch={onSearch}
            />
          </Box>
        </ModalHeader>
        <ModalCloseButton />
        <Divider />
        {loading && (
          <Center h="100px" width="100%">
            <Spinner />
          </Center>
        )}
        <ModalBody p="5" maxH="lg" overflow="auto">
          {!loading && data && !!data.rows.length ? (
            <Table variant="simple">
              <Thead bg="grayV2.100">
                <Tr>
                  {columnKeys.map((key, index) => {
                    return <Th key={index}>{convertCase(key)}</Th>;
                  })}
                </Tr>
              </Thead>
              <Tbody>
                {data.rows.map((row, index) => (
                  <Tr
                    key={index}
                    cursor="pointer"
                    onClick={() =>
                      window.open(
                        `${window.location.origin}${urls.person}/${
                          row.id ?? row.person_id
                        }`,
                        "_blank"
                      )
                    }
                  >
                    {columnKeys.map((key, index) => {
                      return <Td key={index}>{row[key]}</Td>;
                    })}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            !loading && (
              <Center h="100px" w="100%">
                <Text>No Results</Text>
              </Center>
            )
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
